import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const url = e.target.value;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const closeMobile = () => setMobileOpen(false);

  const navTextClass = scrolled
    ? "text-foreground-950"
    : "text-white";

  const navBgClass = scrolled
    ? "bg-background-50/95 backdrop-blur-lg border-b border-background-200/60"
    : "bg-transparent";

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              className={`font-heading text-lg font-semibold tracking-wide whitespace-nowrap transition-colors duration-300 ${navTextClass}`}
            >
              itaobuyspreadsheet
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/"
                className={`px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "text-foreground-700 hover:bg-background-100"
                    : "text-white/85 hover:text-white hover:bg-white/10"
                }`}
              >
                Home
              </Link>
              <Link
                to="/articles/itaobuy-sheet-shopping-guide"
                className={`px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "text-foreground-700 hover:bg-background-100"
                    : "text-white/85 hover:text-white hover:bg-white/10"
                }`}
              >
                Shopping Guide
              </Link>

              <select
                onChange={handleCategoryChange}
                defaultValue=""
                className={`mx-1 px-4 py-2 text-sm rounded-full border cursor-pointer transition-all duration-300 appearance-none bg-no-repeat bg-[length:12px] bg-[right_14px_center] ${
                  scrolled
                    ? "bg-background-50 border-background-200 text-foreground-700"
                    : "bg-white/15 border-white/25 text-white"
                }`}
                style={{
                  backgroundImage: scrolled
                    ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23555555' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`
                    : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                }}
              >
                <option value="" disabled>Browse Categories</option>
                {categories.map((cat) => (
                  <option key={cat.name} value={cat.url}>{cat.name}</option>
                ))}
              </select>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`px-4 py-2 text-sm font-medium rounded-full border transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "border-foreground-300 text-foreground-700 hover:bg-foreground-50"
                    : "border-white/40 text-white hover:bg-white/10"
                }`}
              >
                itaobuy sheet
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "bg-foreground-950 text-background-50 hover:bg-foreground-800"
                    : "bg-white text-foreground-950 hover:bg-white/90"
                }`}
              >
                buy it now
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-xs"></i>
              </a>
            </div>

            {/* Mobile: CTA + Hamburger */}
            <div className="flex md:hidden items-center gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "bg-foreground-950 text-background-50"
                    : "bg-white text-foreground-950"
                }`}
              >
                buy it now
              </a>
              <button
                onClick={() => setMobileOpen(true)}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  scrolled ? "text-foreground-950 hover:bg-background-100" : "text-white hover:bg-white/10"
                }`}
                aria-label="Open menu"
              >
                <i className="ri-menu-line w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Slide-out Panel */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 md:hidden ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={closeMobile}
        ></div>
        <div
          className={`absolute top-0 right-0 w-[85vw] max-w-sm h-full bg-background-50 transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-5 h-16 border-b border-background-200/60">
            <span className="font-heading text-base font-semibold text-foreground-950">Menu</span>
            <button
              onClick={closeMobile}
              className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>

          <div className="px-5 py-4 space-y-1">
            <Link to="/" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
              <i className="ri-home-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              Home
            </Link>
            <Link to="/articles/itaobuy-sheet-shopping-guide" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
              <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              Shopping Guide
            </Link>
          </div>

          <div className="px-5 py-2">
            <p className="px-3 text-[11px] font-semibold uppercase tracking-widest text-foreground-400 mb-2">
              Categories
            </p>
            <div className="grid grid-cols-2 gap-1 max-h-[50vh] overflow-y-auto pr-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="px-5 py-4 border-t border-background-200/60 mt-2 space-y-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium rounded-full border border-foreground-300 text-foreground-700 hover:bg-foreground-50 transition-colors cursor-pointer"
            >
              itaobuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}